import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { Menu, Bell, User, LayoutDashboard, Sparkles, GitBranch, Building2 } from 'lucide-react';
import {
  DASHBOARD_MERLIN_ENABLED,
  DASHBOARD_QXO_ENABLED,
  DASHBOARD_VALUE_CHAIN_ENABLED,
} from '@/config/features';

type DashboardTab = 'overview' | 'merlin' | 'value-chain' | 'qxo';

type Props = {
  children: ReactNode;
  activeTab: DashboardTab;
};

const TABS: Array<{
  id: DashboardTab;
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
}> = [
  { id: 'overview', label: 'Overview', path: '/dashboard', icon: LayoutDashboard },
  { id: 'merlin', label: 'Merlin', path: '/merlin', icon: Sparkles },
  { id: 'value-chain', label: 'Value Chain', path: '/dashboard/value-chain', icon: GitBranch },
  { id: 'qxo', label: 'QXO', path: '/dashboard/qxo', icon: Building2 },
];

export function DashboardShell({ children, activeTab }: Props) {
  const navigate = useNavigate();
  const location = useLocation();

  const visibleTabs = TABS.filter((tab) => {
    if (tab.id === 'merlin') return DASHBOARD_MERLIN_ENABLED;
    if (tab.id === 'value-chain') return DASHBOARD_VALUE_CHAIN_ENABLED;
    if (tab.id === 'qxo') return DASHBOARD_QXO_ENABLED;
    return true;
  });

  const handleLogout = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="bg-white border-b border-gray-200"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-8">
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Menu"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="tracking-tight">Dashboard</h2>
            </div>

            <div className="flex items-center gap-4">
              <button
                type="button"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                aria-label="Notifications"
              >
                <Bell className="w-5 h-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-black rounded-full" />
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Account"
              >
                <User className="w-5 h-5" />
              </button>
            </div>
          </div>

          {visibleTabs.length > 1 ? (
            <nav
              className="mt-4 flex gap-2"
              aria-label="Dashboard sections"
            >
              {visibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive =
                  activeTab === tab.id ||
                  (tab.id === 'overview' && location.pathname === '/dashboard') ||
                  (tab.id === 'value-chain' && location.pathname.startsWith('/dashboard/value-chain')) ||
                  (tab.id === 'qxo' && location.pathname.startsWith('/dashboard/qxo'));

                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => navigate(tab.path)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          ) : null}
        </div>
      </motion.header>

      {children}
    </div>
  );
}
